import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { addChecked, expand } from "slices/discovery/actions/theme";
import {
  collapseTheme,
  uncheckAndCollapse,
} from "slices/discovery/discoverySlice";
import Checkbox, {
  checkboxStates,
} from "@visiblethread/react-components/dist/components/Checkbox";
import { checkedTypes } from "constants/types";
import { calculateThemeFrequency } from "utils/discovery";
import { ReactComponent as PlusIcon } from "icons/plus-grey.svg";
import { ReactComponent as MinusIcon } from "icons/minus-grey.svg";
import ProgressBar from "components/ProgressBar";
import Gradient from "components/Gradient";
import Spinner from "@visiblethread/react-components/dist/components/Spinner";
import { categoryProps } from "./constants";
import Ellipsis from "components/Ellipsis";

const checkedState = (categories, category, isSearch, filterView, showAll) => {
  let nps = categories.filter(
    (t) => t.GroupName === category.GroupName && t.NounPhrase
  );

  if (filterView) {
    nps = nps.filter((np) => np.FilteredByFilter);
  }

  if (isSearch) {
    nps = nps.filter((np) => np.FilteredBySearch);
  }

  if (!showAll) {
    nps = nps.filter((np) => np.selected);
  }

  const checkedNps = nps.filter((ct) => ct.checked);

  if (checkedNps.length && nps.length === checkedNps.length) {
    return checkedTypes.CHECKED;
  }
  if (checkedNps.length) {
    return checkedTypes.INDETERMINATE;
  }
  return checkedTypes.EMPTY;
};

const Category = ({
  category,
  categories,
  isCollapsed,
  maxOccurrences,
  alignCollapseButtonToRight = false,
}) => {
  const dispatch = useDispatch();
  const [collapsed, setCollapsed] = useState(isCollapsed);
  const [loading, setLoading] = useState(false);
  const firstRun = useRef(true);
  const documentView =
    useSelector((state) => state.discovery.config.documentId) !== undefined;

  const showAll = useSelector((state) => state.discovery.config.showAll);
  const isSearch = useSelector((state) => state.discovery.config.isSearch);
  const filterView = useSelector((state) => state.discovery.config.filterView);
  const filterType = useSelector((state) => state.discovery.config.filterType);

  const checkboxRef = useRef();

  const noOccurrences =
    category.Occurrences === undefined || category.Occurrences === 0;

  const frequency = () => {
    let fq;
    if (isSearch || filterView || !showAll) {
      fq = calculateThemeFrequency(
        categories,
        category,
        showAll,
        isSearch,
        filterView
      );
    } else {
      fq = category.Occurrences;
    }

    if (documentView) {
      return <Gradient content={fq} maxOccurrences={maxOccurrences} />;
    }

    return (
      <>
        <ProgressBar
          current={fq}
          max={maxOccurrences}
          width="50px"
          height="5px"
        />
        <span>{fq.toLocaleString()}</span>
      </>
    );
  };

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    dispatch(collapsed ? collapseTheme(category.GroupName) : expand(category));
  }, [collapsed]);

  useEffect(() => {
    if (checkboxRef.current) {
      switch (
        checkedState(categories, category, isSearch, filterView, showAll)
      ) {
        case checkboxStates.checked:
          checkboxRef.current.checked = true;
          checkboxRef.current.indeterminate = false;
          break;
        case checkboxStates.indeterminate:
          checkboxRef.current.checked = false;
          checkboxRef.current.indeterminate = true;
          break;
        default:
          checkboxRef.current.checked = false;
          checkboxRef.current.indeterminate = false;
      }
    }
  });

  const handleChange = (e) => {
    setLoading(true);
    if (e.target.checked) {
      dispatch(addChecked(category));
    } else {
      dispatch(uncheckAndCollapse(category));
    }
  };

  const getExpandCollapseIcon = () => {
    const props = {
      width: 8,
      height: 8,
      className: "plus_minus_icon",
    };
    return collapsed ? <PlusIcon {...props} /> : <MinusIcon {...props} />;
  };

  const handleExpandCollapse = () => {
    setLoading(true);
    setCollapsed(!collapsed);
  };

  const text = () => (
    <>
      <Checkbox
        state={checkedState(
          categories,
          category,
          isSearch,
          filterView,
          showAll
        )}
        handleChange={handleChange}
        id={category}
        value={category.GroupName}
        checkboxRef={checkboxRef}
      />
      <button
        onClick={handleExpandCollapse}
        type="button"
        className={`category__text__button${noOccurrences ? " greyed" : ""}`}
        data-testid="category-text-button"
      >
        <Ellipsis text={category.GroupName} />
        <div
          className="category__text__button__icon"
          style={{
            marginLeft: alignCollapseButtonToRight ? "auto" : "",
          }}
          data-testid="category-text-icon-wrapper"
        >
          {!loading ? getExpandCollapseIcon() : <Spinner size="1rem" />}
        </div>
      </button>
    </>
  );

  if ((isSearch || filterView) && category.SearchOccurrences === 0) return null;

  return (
    <div
      className={`category${noOccurrences ? " greyed" : ""}`}
      data-testid="category"
    >
      <div className="category__text">{text()}</div>
      <div className="category__frequency">{frequency()}</div>
    </div>
  );
};
export default Category;

Category.propTypes = {
  category: PropTypes.shape(categoryProps).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape(categoryProps).isRequired)
    .isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  maxOccurrences: PropTypes.number,
  alignCollapseButtonToRight: PropTypes.bool,
};
